/** O material do preenchimento: fundo e trama.
 *
 *  A trama desenha-se no espaço do ecrã (gl_FragCoord), como as tramas
 *  de desenho dos programas de CAD: o espaçamento é o mesmo em píxeis a
 *  qualquer distância, e uma laje lá ao fundo não vira uma mancha nem
 *  ganha moiré. O fundo e a trama são cores planas, sem luz, para o
 *  corte se ler como desenho e não como mais uma face.
 *
 *  Um material por estilo, guardado: os elementos com o mesmo estilo vão
 *  na mesma malha. Os planos de corte do renderizador aplicam-se como a
 *  qualquer MeshBasicMaterial (os chunks do three ficam). */

import * as THREE from 'three'

import type { EstiloDeCorte, Trama } from './estilosDeCorte'
import { chaveDoEstilo } from './estilosDeCorte'

/** Espaçamento da trama, em píxeis de CSS. */
export const ESPACAMENTO_PX = 9

const CODIGO: Record<Trama, number> = {
  cheio: 0,
  diagonal: 1,
  'diagonal-inversa': 2,
  cruzada: 3,
  horizontal: 4,
  vertical: 5,
  pontos: 6,
  betao: 7,
  isolamento: 8,
}

const GLSL = /* glsl */ `
uniform vec3 vn2rFundo;
uniform vec3 vn2rTraco;
uniform float vn2rEsp;
uniform float vn2rPx;

float vn2rLinha(float d, float esp, float largura) {
  float m = mod(d, esp);
  float dist = min(m, esp - m);
  return 1.0 - smoothstep(largura * 0.5, largura * 0.5 + 0.9, dist);
}

float vn2rAcaso(vec2 p) {
  return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453);
}

float vn2rTrama(vec2 p) {
  float esp = vn2rEsp;
  float l = vn2rPx;
  // gl_FragCoord tem o y para cima: x - y constante dá "/", a diagonal
  // dos CAD (ANSI31), a mesma do SVG e do PDF das Vistas 2D.
#if VN2R_TRAMA == 1
  return vn2rLinha((p.x - p.y) * 0.70710678, esp, l);
#elif VN2R_TRAMA == 2
  return vn2rLinha((p.x + p.y) * 0.70710678, esp, l);
#elif VN2R_TRAMA == 3
  return max(vn2rLinha((p.x + p.y) * 0.70710678, esp, l), vn2rLinha((p.x - p.y) * 0.70710678, esp, l));
#elif VN2R_TRAMA == 4
  return vn2rLinha(p.y, esp, l);
#elif VN2R_TRAMA == 5
  return vn2rLinha(p.x, esp, l);
#elif VN2R_TRAMA == 6
  vec2 c = mod(p, esp) - esp * 0.5;
  return 1.0 - smoothstep(0.9 * vn2rPx, 1.8 * vn2rPx, length(c));
#elif VN2R_TRAMA == 7
  // Betão: pontos espalhados ao acaso, um por célula, alguns maiores.
  vec2 cel = floor(p / (esp * 0.8));
  vec2 dentro = fract(p / (esp * 0.8));
  float a = vn2rAcaso(cel);
  vec2 centro = vec2(vn2rAcaso(cel + 3.1), vn2rAcaso(cel + 7.7)) * 0.7 + 0.15;
  float r = a > 0.85 ? 0.22 : 0.11;
  return 1.0 - smoothstep(r, r + 0.08, length(dentro - centro));
#elif VN2R_TRAMA == 8
  // Isolamento: ondas.
  float onda = p.y + sin(p.x / esp * 3.14159) * esp * 0.45;
  return vn2rLinha(onda, esp * 1.4, l);
#else
  return 0.0;
#endif
}
`

const materiais = new Map<string, THREE.MeshBasicMaterial>()

function linear(hex: string): THREE.Color {
  // A Color do three converte o hex de sRGB para linear, e a saída
  // converte de volta: a cor no ecrã é a do hex.
  return new THREE.Color(hex)
}

/** O material de um estilo (guardado e reutilizado). */
export function materialDaTrama(estilo: EstiloDeCorte, pixelRatio: number): THREE.MeshBasicMaterial {
  const chave = chaveDoEstilo(estilo)
  let mat = materiais.get(chave)
  if (!mat) {
    mat = new THREE.MeshBasicMaterial({ color: estilo.fundo, side: THREE.DoubleSide })
    const uniforms = {
      vn2rFundo: { value: linear(estilo.fundo) },
      vn2rTraco: { value: linear(estilo.corDaTrama) },
      vn2rEsp: { value: ESPACAMENTO_PX * pixelRatio },
      vn2rPx: { value: pixelRatio },
    }
    mat.userData.vn2rUniforms = uniforms
    mat.defines = { VN2R_TRAMA: CODIGO[estilo.trama] }
    mat.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, uniforms)
      shader.fragmentShader = shader.fragmentShader
        .replace('void main() {', `${GLSL}\nvoid main() {`)
        .replace(
          '#include <opaque_fragment>',
          `#include <opaque_fragment>
gl_FragColor.rgb = mix(vn2rFundo, vn2rTraco, vn2rTrama(gl_FragCoord.xy));`,
        )
    }
    mat.customProgramCacheKey = () => `vn2rTrama:${estilo.trama}`
    materiais.set(chave, mat)
  }
  const u = mat.userData.vn2rUniforms as { vn2rEsp: { value: number }; vn2rPx: { value: number } }
  u.vn2rEsp.value = ESPACAMENTO_PX * pixelRatio
  u.vn2rPx.value = pixelRatio
  return mat
}
